import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Alert, Card, Form, Layout, Tabs } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { Routes } from '../../../routes';
import useHasError from '../../hooks/useHasError'; 
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import useIsLoading from '../../hooks/useIsLoading';
import useCourse from '../../gql/hooks/useCourse';
import Spinner from '../../components/Spinner/Spinner';
import TabILT from '../../components/TabILT/TabILT';
import TabTags from '../../components/TabTags/TabTags';
import TabMedia from '../../components/TabMedia/TabMedia';
import TabBasics from '../../components/TabBasics/TabBasics';
import TabPoints from '../../components/TabPoints/TabPoints';
import TabCategories from '../../components/TabCategories/TabCategories';
import TabCertificates from '../../components/TabCertificates/TabCertificates';
import TabPrerequisites from '../../components/TabPrerequisites/TabPrerequisites';
import TabAudience from '../../components/TabAudience/TabAudience';
import CourseTabContent from './CourseTabContent'; 
import StrongTag from '../../components/StrongTag/StrongTag';
import { flags } from '../../../featureFlags/flags'; 
import { useFeatureFlags } from '../../../featureFlags';
import useILT from '../../gql/hooks/useILT';
import './styles.less';

// fields reset to the server values whenever the course (re)loads
const RESETTABLE_FIELDS = [ 'learningObjects', 'published', 'resources', 'isSequential' ];

const initialFormValues = {
    id: '',                      // Matches what is on server 
    type: 'course',              // Do not save, internal use only
    hasRevision: false,          // Derived from what is on server at courseRevision
    revisionLearningObjects: [], // Derived from what is on server at courseRevision 
    isSequential: false,         // whether the learning objects must be completed in sequence. gated by company variable
    
    // Legacy, don't expect these to ever change
    metadata: {},
    isPublic: true, 
         
    locales: [],                // Matches what is on server
    locale: 'en-US',            // Do not save, internal use only
    localesModified: [],        // Do not save, internal use only
    localesAreModified: false,  // Do not save, internal use only
    localesForSaving: [],       // Do not save, internal use only
        
    heroImage: undefined,            // Do not save, matches what is on server
    heroImageIsModified: false,      // Do not save, internal use only
    heroImageModified: undefined,    // Upload this if exists, or if heroImageIsModified and this is false save NO hero image

    thumbnailImage: undefined,          // Do not save, matches what is on server
    thumbnailImageIsModified: false,    // Do not save, internal use only
    thumbnailImageModified: undefined,  // Upload this if exists, or if thumbnailImageIsModified and this is false save NO thumbnail image

    points: 0,                          // Matches what is on server 
    pointsModified: undefined,          // Do not save, internal use only         
    pointsIsModified: false,            // Do not save, internal use only
    
    weight: 0,                          // Matches what is on server 
    weightModified: undefined,          // Do not save, internal use only         
    weightIsModified: false,            // Do not save, internal use only
    
    isFeatured: false,                  // Matches what is on server 
    isFeaturedModified: undefined,      // Do not save, internal use only         
    isFeaturedIsModified: false,        // Do not save, internal use only
    
    // Uses syncObjectRelationships
    brandIds: [],                       // Matches what is on server
    brandIdsModified: [ ],              // Do not save, internal use only
    brandIdsAreModified: false,         // Do not save, internal use only
    brandIdDefault: undefined,          // Used only when creating w one brand
        
    // Uses syncObjectRelationships
    tagIds: [],                         // Matches what is on server
    tagIdsModified: [],                 // Do not save, internal use only
    tagIdsAreModified: false,           // Do not save, internal use only
        
    // Uses syncObjectRelationships
    categoryIds: [],                    // Matches what is on server
    categoryIdsModified: [],            // Do not save, internal use only
    categoryIdsAreModified: false,      // Do not save, internal use only
    
    // Uses syncObjectRelationships
    segmentIds: [],                    // Matches what is on server
    segmentIdsModified: [],            // Do not save, internal use only
    segmentIdsAreModified: false,      // Do not save, internal use only
    
    // Uses syncObjectRelationships
    companyRelationshipIds: [],                    // Matches what is on server
    companyRelationshipIdsModified: [],            // Do not save, internal use only
    companyRelationshipIdsAreModified: false,      // Do not save, internal use only
    
    // Uses syncObjectRelationships
    productIds: [],                    // Matches what is on server
    productIdsModified: [],            // Do not save, internal use only
    productIdsAreModified: false,      // Do not save, internal use only
    
    // Uses syncObjectRelationships
    learningProfileIds: [],                    // Matches what is on server
    learningProfileIdsModified: [],            // Do not save, internal use only
    learningProfileIdsAreModified: false,      // Do not save, internal use only

    // Uses syncObjectRelationships
    employeeLocaleIds: [],                    // Matches what is on server
    employeeLocaleIdsModified: [],            // Do not save, internal use only
    employeeLocaleIdsAreModified: false,      // Do not save, internal use only
    
    // Uses syncObjectRelationships
    localeIds: [],                    // Matches what is on server
    localeIdsModified: [],            // Do not save, internal use only
    localeIdsAreModified: false,      // Do not save, internal use only

    // Uses some odd same shit
    certTemplates: [],                // Matches what is on server 
    certificateIds: [],               // Matches what is on server, but ONLY the IDs
    certificateIdsModified: [],       // Do not save, internal use only
    certificateIdsAreModified: false, // Do not save, internal use only

    prerequisiteSet: undefined,       // Matches what is on server 
    prerequisiteOf: [],               // Matches what is on server, but pulled from isPrerequisiteOf
    prerequistieIds: [],              // Matches what is on server, but pulled from hasPrerequisites
    prerequistieAll: false,           // Matches what is on server, but pulled from prerequisiteSet
   
    prerequistieIdsModified: [],      // Do not save, internal use only 
    prerequisiteAllModified: false,   // Do not save, internal use only 
    prerequistiesAreModified: false,  // Do not save, internal use only 

    published: false,                 // Matches what is on server
    publishedModified: undefined,     // Do not save, internal use only
    publishedIsModified: false,       // Do not save, internal use only

    isILT: false,                     // Matches what is on server
    isILTModified: undefined,         // Do not save, internal use only
    isILTIsModified: false,           // Do not save, internal use only
    iltID: undefined                  // Do not save, internal use only
};

const CourseEditPage = ( { route: { params: { courseId } } } ) => { 
    const [ form ] = Form.useForm();
    const basicsTabRef = useRef( null );
    const contectTabRef = useRef( null );
    const mediaTabRef = useRef( null );
    const certificatesTabRef = useRef( null );
    const tagsTabRef = useRef( null );
    const ILTTabRef = useRef( null );
    const categoriesTabRef = useRef( null );
    const prerequisitesTabRef = useRef( null );
    const pointsTabRef = useRef( null ); 
    const TabAudienceRef = useRef( null );
    const [ isCreating, setIsCreating ] = useState( !courseId );
    const [ isILT, setIsILT ] = useState( false );
    const [ activeTab, setActiveTab ] = useState( 'basics' ); 
    const { loadCourse, courseData, courseError, courseLoading, refetchCourse } = useCourse();
    const { hasError } = useHasError( { items: [ courseError ] } );
    const { isLoading } = useIsLoading( { items: [ courseLoading ] } );
    const [ formReady, setFormReady ] = useState( !courseId );
    const [ id, setId ] = useState( courseId );
    const [ initValues ] = useState( { ...initialFormValues } );
    const [ showPoints ] = useFeatureFlags( [ flags.COURSE_POINTS ] );
    const [ showCategories ] = useFeatureFlags( [ flags.CATEGORIES ] );
    const [ showCertificates ] = useFeatureFlags( [ flags.CERTIFICATES ] ); 
    const [ isSaving, setIsSaving ] = useState( false );
    const [ nextTab, setNextTab ] = useState();
    const [ isPublished, setIsPublished ] = useState( false );
    const [ tabBasicsLabel, setTabBasicsLabel ] = useState( 'Basics' );
    const [ tabContentLabel, setTabContentLabel ] = useState( 'Content' );
    const [ tabAudienceLabel, setTabAudienceLabel ] = useState( 'Audience' );
    const [ tabMediaLabel, setTabMediaLabel ] = useState( 'Media' );
    const [ tabCertificatesLabel, setTabCertificatesLabel ] = useState( 'Certificates' );
    const [ tabCategoriesLabel, setTabCategoriesLabel ] = useState( 'Categories' );
    const [ tabTagsLabel, setTabTagsLabel ] = useState( 'Tags' );
    const [ tabILTLabel, setTabILTLabel ] = useState( 'Instructor Led Training' );
    const [ tabPrerequisitesLabel, setTabPrerequisitesLabel ] = useState( 'Prerequisites' );
    const [ tabPointsLabel, setTabPointsLabel ] = useState( 'Points' );
    const { loadILT } = useILT();
    const [ ILTTabCheck, setILTTabCheck ] = useState( false );
    
    const breadcrumbs = [
        { name: 'Course Studio', path: Routes.Home.buildPath() },
        { name: 'Learning Content', path: Routes.LearningContentNav.buildPath() },
        { name: 'Courses', path: Routes.Courses.buildPath() },
        { name: isCreating ? 'Create' : 'Edit' }
    ];

    const preserveFields = useRef( [] ); // fields the next reload should leave untouched

    function hasAnyValues( obj ) {
        // accepts objects or arrays
        return obj && ( ( Array.isArray( obj ) && obj.length > 0 ) || Object.values( obj ).some( v => v?.length > 0 ) );
    }

    const updateTabCheckmarks = ( values ) => { 
        if ( values?.id  || form.getFieldValue( 'id' ) ) {
            setTabBasicsLabel( ( <><CheckOutlined />Basics</> ) );
        } else {
            setTabBasicsLabel( 'Basics' );
        }  

        if ( ILTTabCheck ) {
            setTabILTLabel( ( <><CheckOutlined />Instructor Led Training</> ) );
        } else {
            setTabILTLabel( 'Instructor Led Training' );
        }  

        if ( hasAnyValues( values?.learningObjects ) || hasAnyValues( values?.resources ) || hasAnyValues( form.getFieldsValue( [ 'learningObjects', 'resources' ] ) ) ) {
            setTabContentLabel( ( <><CheckOutlined />Content</> ) );
        } else {
            setTabContentLabel( 'Content' );
        }

        if ( values?.segmentIds?.length > 0 || form.getFieldValue( 'segmentIds' )?.length > 0 ||
            values?.productIds?.length > 0 || form.getFieldValue( 'productIds' )?.length > 0 ||
            values?.companyRelationshipIds?.length > 0 || form.getFieldValue( 'companyRelationshipIds' )?.length > 0 ||
            values?.employeeLocaleIds?.length > 0 || form.getFieldValue( 'employeeLocaleIds' )?.length > 0 ||
            values?.localeIds?.length > 0 || form.getFieldValue( 'localeIds' )?.length > 0 ) {
            setTabAudienceLabel( ( <><CheckOutlined />Audience</> ) );
        } else {
            setTabAudienceLabel( 'Audience' );
        }

        if ( values?.thumbnailImage || form.getFieldValue( 'thumbnailImage' ) ||
            values?.heroImage || form.getFieldValue( 'heroImage' ) ) {
            setTabMediaLabel( ( <><CheckOutlined />Media</> ) );
        } else {
            setTabMediaLabel( 'Media' );
        }      

        if ( values?.certificateIds?.length > 0 || form.getFieldValue( 'certificateIds' )?.length > 0 ) {
            setTabCertificatesLabel( ( <><CheckOutlined />Certificates</> ) );
        } else {
            setTabCertificatesLabel( 'Certificates' );
        }

        if ( values?.categoryIds?.length > 0 || form.getFieldValue( 'categoryIds' )?.length > 0 ) {
            setTabCategoriesLabel( ( <><CheckOutlined />Categories</> ) );
        } else {
            setTabCategoriesLabel( 'Categories' );
        }  

        if ( values?.tagIds?.length > 0 || form.getFieldValue( 'tagIds' )?.length > 0 ) {
            setTabTagsLabel( ( <><CheckOutlined />Tags</> ) );
        } else {
            setTabTagsLabel( 'Tags' );
        }  

        if ( values?.prerequistieIds?.length > 0 || form.getFieldValue( 'prerequistieIds' )?.length > 0 ) {
            setTabPrerequisitesLabel( ( <><CheckOutlined />Prerequisites</> ) );
        } else {
            setTabPrerequisitesLabel( 'Prerequisites' );
        }  

        if ( values?.points > 0 || form.getFieldValue( 'points' ) > 0 ) {
            setTabPointsLabel( ( <><CheckOutlined />Points</> ) );
        } else {
            setTabPointsLabel( 'Points' );
        }  
    };

    const createdCourseCallback = ( ) => {
        setId( form.getFieldValue( 'id' ) );
        setIsCreating( false ); 
        history.replaceState( {}, '', 
            window.location.href.replace( 'create', `edit/${  form.getFieldValue( 'id' ) }` ) );
    };

    /*************************************
     *  ANCHOR Load course handling
     *************************************/
    
    useEffect( () => {
        if ( isCreating || !id ) {
            return;
        }
        
        loadCourse( id );
    }, [ isCreating, id ] );
    
    useEffect( () => getInitialValues(), [ courseData ] );
        
    function getInitialValues() {
        if ( !courseData ) {
            return;
        }

        initValues.learningObjects = courseData.learningObjects.map( l => {
            if ( courseData.courseRevision?.learningObjects?.find( r => r.id === l.id ) ) {
                return { ...l, isPermanent: true };
            }
            return { ...l, isPermanent: false };
        } );

        initValues.id = courseData.id;      
        initValues.weight = courseData.weight;      
        initValues.brandIds = courseData.brandIds;      
        initValues.isFeatured = courseData.isFeatured;      
        initValues.heroImage = courseData.heroImage;      
        initValues.published = courseData.isPublished;
        initValues.thumbnailImage = courseData.thumbnailImage;      
        initValues.points = courseData.points;      
        initValues.isSequential = courseData.isSequential;      
        initValues.resources = ( courseData.resourceIds || [] ).map( id => ( { id } ) );      
        initValues.tagIds = courseData.tagIds;      
        initValues.categoryIds = courseData.categoryIds;      
        initValues.segmentIds = courseData.segmentIds;      
        initValues.productIds = courseData.productIds;      
        initValues.companyRelationshipIds = courseData.companyRelationshipIds;      
        initValues.certTemplates = courseData.courseCertificates;     
        initValues.learningProfileIds = courseData.learningProfileIds;      
        initValues.employeeLocaleIds = courseData.employeeLocaleIds;      
        initValues.localeIds = courseData.localeIds;      
        initValues.certificateIds = courseData.courseCertificates?.map( i => i.certificateTemplate?.id ) || [];    
        initValues.locales = courseData.locales.map( i => 
            ( { locale: i.locale, name: i.name, description: i.description } ) );      
        initValues.prerequisiteSet = courseData.prerequisiteSet;
        initValues.prerequistieAll = courseData.prerequisiteSet?.operator === 'AND' ? true : false;
        initValues.prerequisiteStatus = courseData.prerequisiteStatus;
        initValues.prerequistieIds = courseData.prerequisiteStatus.hasPrerequisites || [];
        initValues.prerequisiteOf = courseData.prerequisiteStatus.isPrerequisiteOf || [];
        initValues.hasRevision = courseData.courseRevision && courseData.courseRevision !== null;
        initValues.revisionLearningObjects = courseData.courseRevision?.learningObjects?.map( i => i.id ) || [];
        form.setFieldsValue( { revisionLearningObjects: initValues.revisionLearningObjects } );     
        
        initValues.isILT = courseData.isIlt ;
        
        if ( initValues.isILT ) {
            if ( courseData.learningObjects?.[ 0 ] ) {
                initValues.iltID = courseData.learningObjects?.[ 0 ].content?.instructorLedTrainingId;
                form.setFieldsValue( { iltID: initValues.iltID } );     
                
                setTimeout( () => {
                    updateILTCheckmark();
                }, 1500 );
            }
        }

        setIsILT( initValues.isILT );
        setIsPublished( initValues.published  );
        updateTabCheckmarks( initValues );

        // only on a reload - on first load the <Form> is not mounted yet, so a reset would write
        //   undefined into the store before the Form gets to apply initValues
        if ( formReady ) {
            resetFields();
        }
        setFormReady( true );
    }

    // this should be called every time a save happens and the course is refreshed for every field.
    //   for now though were working a partial solution so just reset the one that I know wont break
    //   everything.
    function resetFields() {
        // skip any field whose save just failed so the user's unsaved edits survive the reload
        form.resetFields( RESETTABLE_FIELDS.filter( f => !preserveFields.current.includes( f ) ) );
        preserveFields.current = [];
    }
    
    const reloadCourse = async ( unsuccessfulFields = [] ) => {
        if ( isCreating || !id ) {
            return;
        }
        
        preserveFields.current = unsuccessfulFields;
        await refetchCourse(); 
    };

    const courseHeaderName = useMemo( () => {
        if ( isCreating ) {
            return 'Create Course';
        }
        const revisionCount = courseData?.courseRevision?.revisionCount || '';
        
        const EDIT_NAME = 'Edit Course';

        let locales =  courseData?.locales?.map( i => 
            ( { locale: i.locale, name: i.name, description: i.description } ) );  
    
        const courseName = locales ? locales.find( i => i.locale === 'en-US' )?.name || '' : '';
            
        if ( !revisionCount ) {
            return `${ EDIT_NAME } ${ courseName ? ' - ' : '' } ${ courseName }`;
        } else {
            return ( <>
                <span>{ `${ EDIT_NAME } ${ courseName ? ' - ' : '' } ${ courseName }` }</span>
                <br/>
                <small>{ `Revision ${ revisionCount }` }</small>
            </> );
        }
    }, [ isCreating, formReady, form, courseData ] );
 
    /*************************************
     *  ANCHOR Event handlers
     *************************************/

    const handleTabChange = ( tab ) => {
        if ( activeTab === 'basics' ) { 
            if ( basicsTabRef.current.isModified() ) {
                setNextTab( tab );
                basicsTabRef.current.showSaveConfirm();
                return;
            }  else {
                setActiveTab( tab );
                return;
            }
        } 
        
        if ( activeTab === 'content' ) { 
            if ( contectTabRef.current.isModified() ) {
                setNextTab( tab );
                contectTabRef.current.showSaveConfirm();
                return;
            }  else {
                contectTabRef.current?.reset();
                setActiveTab( tab );
                return;
            }
        } 

        if ( activeTab === 'ILT' ) { 
            if ( ILTTabRef.current.isModified() ) {
                setNextTab( tab );
                ILTTabRef.current.showSaveConfirm();
                return;
            }  else {
                setActiveTab( tab );
                return;
            }
        } 

        if ( activeTab === 'certificates' ) { 
            if ( certificatesTabRef.current.isModified() ) {
                setNextTab( tab );
                certificatesTabRef.current.showSaveConfirm();
                return;
            }  else {
                setActiveTab( tab );
                return;
            }
        } 

        if ( activeTab === 'media' ) { 
            if ( mediaTabRef.current.isModified() ) {
                setNextTab( tab );
                mediaTabRef.current.showSaveConfirm();
                return;
            }  else {
                setActiveTab( tab );
                return;
            }
        } 

        if ( activeTab === 'tags' ) { 
            if ( tagsTabRef.current.isModified() ) {
                setNextTab( tab );
                tagsTabRef.current.showSaveConfirm();
                return;
            }  else {
                setActiveTab( tab );
                return;
            }
        } 

        if ( activeTab === 'categories' ) { 
            if ( categoriesTabRef.current.isModified() ) {
                setNextTab( tab );
                categoriesTabRef.current.showSaveConfirm();
                return;
            }  else {
                setActiveTab( tab );
                return;
            }
        } 

        if ( activeTab === 'prerequisites' ) { 
            if ( prerequisitesTabRef.current.isModified() ) {
                setNextTab( tab );
                prerequisitesTabRef.current.showSaveConfirm();
                return;
            }  else {
                setActiveTab( tab );
                return;
            }
        } 

        if ( activeTab === 'points' ) { 
            if ( pointsTabRef.current.isModified() ) {
                setNextTab( tab );
                pointsTabRef.current.showSaveConfirm();
                return;
            }  else {
                setActiveTab( tab );
                return;
            }
        }

        if ( activeTab === 'segmentation' ) { 
            if ( TabAudienceRef.current.isModified() ) {
                setNextTab( tab );
                TabAudienceRef.current.showSaveConfirm();
                return;
            }  else {
                setActiveTab( tab );
                return;
            }
        }

        setActiveTab( tab );
    };

    // proceed = false keeps the user on the current tab instead of moving on to nextTab - used when
    //   a save from the confirm dialog only partly succeeded
    const handleIsSavingCallback = ( saving, proceed = true ) => {
        setIsSaving( saving );
        setIsPublished( form.getFieldValue( 'published' ) );

        if ( nextTab && !saving ) {
            if ( proceed ) {
                setActiveTab( nextTab );
            }
            setNextTab( undefined );
        }

        updateTabCheckmarks();

        if ( isILT ) {
            updateILTCheckmark();
        }
    };

    const updateILTCheckmark = async () => {
        const result = await loadILT( form.getFieldValue( 'iltID' ) );
        const sessions = result?.data?.instructorLedTraining?.sessions;

        if ( sessions && sessions.length > 0 ) {
            setILTTabCheck( true );
        } else {
            setILTTabCheck( false );
        }
    };

    useEffect( () => {
        updateTabCheckmarks();
    }, [ ILTTabCheck ] );

    /*************************************
     *  ANCHOR Render
     *************************************/
   
    return (
        <Layout className="course-edit-page">
            
            { /* Page header and breadcrumbs */ }
            <Breadcrumbs crumbs={ breadcrumbs }/>

            { /* Loading Spinner */ }
            { isLoading &&
                <Spinner/>
            }
                
            { /* Main Content */ }
            { !isLoading &&
                <Card
                    className="course-edit-content"
                    title={ courseHeaderName }
                    extra={ 
                        isCreating ? null : isPublished ? 
                            <StrongTag 
                                color="green"
                                strontText="Published" 
                            />
                            :
                            <StrongTag 
                                color="red"
                                strontText="Unpublished" 
                            />
                    }
                >
                    { /* Error message */ }
                    { hasError && 
                        <Alert 
                            type="error"
                            message="Error"
                            description="An unknown error occurred. Please try again later."
                        />
                    }
                    
                    { !hasError && formReady &&
                        <Form
                            form={ form }
                            name="course-form"
                            layout="vertical"
                            initialValues={ initValues }
                            scrollToFirstError
                        >   
                            <Tabs 
                                type="card"
                                size="large"
                                activeKey={ activeTab }
                                onChange={ handleTabChange }
                                items={
                                    [
                                        {
                                            key: 'basics',
                                            label: tabBasicsLabel,
                                            children: <TabBasics 
                                                form={ form }
                                                ref={ basicsTabRef }
                                                isSavingCallback={ handleIsSavingCallback }
                                                createdCallback={ createdCourseCallback }
                                            />
                                        },
                                        ...( !isCreating && isILT ? 
                                            [ 
                                                {
                                                    key: 'ILT',
                                                    label: tabILTLabel,
                                                    disabled: isSaving,
                                                    children: <TabILT 
                                                        form={ form }
                                                        ref={ ILTTabRef }
                                                        isSavingCallback={ handleIsSavingCallback }
                                                        reloadCourseCallback={ reloadCourse }
                                                    />
                                                }
                                            ] : [] 
                                        ),
                                        ...( !isCreating && !isILT ? 
                                            [ 
                                                {
                                                    key: 'content',
                                                    label: tabContentLabel,
                                                    disabled: isSaving,
                                                    children: <CourseTabContent 
                                                        form={ form }
                                                        initialValues={ initValues }
                                                        courseId={ courseId }
                                                        ref={ contectTabRef }
                                                        isSavingCallback={ handleIsSavingCallback }
                                                        reloadCourseCallback={ reloadCourse }
                                                    />
                                                }
                                            ] : [] 
                                        ),
                                        ...( !isCreating ? 
                                            [ 
                                                {
                                                    key: 'segmentation',
                                                    label: tabAudienceLabel,
                                                    disabled: isSaving,
                                                    children: <TabAudience 
                                                        form={ form }
                                                        loading={ isLoading }
                                                        ref={ TabAudienceRef }
                                                        isSavingCallback={ handleIsSavingCallback }
                                                    />
                                                }
                                            ] : [] 
                                        ),
                                        ...( !isCreating ? 
                                            [ 
                                                {
                                                    key: 'media',
                                                    label: tabMediaLabel,
                                                    disabled: isSaving,
                                                    children: <TabMedia 
                                                        form={ form }
                                                        ref={ mediaTabRef }
                                                        isSavingCallback={ handleIsSavingCallback }
                                                    />
                                                }
                                            ] : [] 
                                        ),
                                        ...( !isCreating && showCategories ? 
                                            [ 
                                                {
                                                    key: 'categories',
                                                    label: tabCategoriesLabel,
                                                    disabled: isSaving,
                                                    children: <TabCategories 
                                                        form={ form }
                                                        ref={ categoriesTabRef }
                                                        isSavingCallback={ handleIsSavingCallback }
                                                    />
                                                }
                                            ] : [] 
                                        ),
                                        ...( !isCreating && showCertificates ? 
                                            [ 
                                                {
                                                    key: 'certificates',
                                                    label: tabCertificatesLabel,
                                                    disabled: isSaving,
                                                    children: <TabCertificates 
                                                        form={ form }
                                                        ref={ certificatesTabRef }
                                                        isSavingCallback={ handleIsSavingCallback }
                                                    />
                                                }
                                            ] : [] 
                                        ),
                                        ...( !isCreating ? 
                                            [ 
                                                {
                                                    key: 'tags',
                                                    label: tabTagsLabel,
                                                    disabled: isSaving,
                                                    children: <TabTags 
                                                        form={ form }
                                                        ref={ tagsTabRef }
                                                        isSavingCallback={ handleIsSavingCallback }
                                                    />
                                                }
                                            ] : [] 
                                        ),
                                        ...( !isCreating ? 
                                            [ 
                                                {
                                                    key: 'prerequisites',
                                                    label: tabPrerequisitesLabel,
                                                    disabled: isSaving,
                                                    children: <TabPrerequisites 
                                                        form={ form } 
                                                        type="courses"
                                                        ref={ prerequisitesTabRef }
                                                        isSavingCallback={ handleIsSavingCallback }
                                                    />
                                                }
                                            ] : [] 
                                        ),
                                        ...( !isCreating && showPoints ? 
                                            [ 
                                                {
                                                    key: 'points',
                                                    label: tabPointsLabel,
                                                    disabled: isSaving,
                                                    children: <TabPoints 
                                                        form={ form }
                                                        ref={ pointsTabRef }
                                                        isSavingCallback={ handleIsSavingCallback }
                                                    />
                                                }
                                            ] : [] 
                                        ),
                                    ]
                                }
                            > 
                            </Tabs>
                        </Form>
                    }
                </Card>
            }
        </Layout>
    );
};

export default CourseEditPage;
